import React, { useEffect, useRef, useState } from 'react';
import { Modal, Table, Rate, Empty, Typography, Tag, Button, Select, Space, Tooltip, Alert, message } from 'antd';
import { WhatsAppOutlined, SendOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Text } = Typography;

const TOP_OPTIONS = [
  { value: '1', label: 'Top 1 (só o 1º)' },
  { value: '3', label: 'Top 3 (pódio)' },
  { value: '5', label: 'Top 5' },
  { value: 'all', label: 'Todos avaliados' },
];

function fmtCooldown(sec) {
  if (sec <= 0) return 'agora';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min ${s}s`;
  return `${s}s`;
}

const GameRatingsModal = ({ open, gameId, gameLabel, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // Broadcast state
  const [topN, setTopN] = useState('3');
  const [broadcastStatus, setBroadcastStatus] = useState({ can_send: true, wait_seconds: 0 });
  const [previewModal, setPreviewModal] = useState({ open: false, text: '', count: 0 });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const countdownRef = useRef(null);

  useEffect(() => {
    if (!open || !gameId) return;
    setLoading(true);
    Promise.all([
      api.get(`/organizer-games/${gameId}/ratings-summary`),
      api.get(`/organizer-games/${gameId}/ratings-broadcast-status`).catch(() => ({ data: { can_send: true } })),
    ]).then(([r, s]) => {
      setRows(r.data?.players || []);
      setBroadcastStatus(s.data || { can_send: true });
    }).catch(err => message.error(err?.response?.data?.error || 'Erro ao carregar avaliações'))
      .finally(() => setLoading(false));
  }, [open, gameId]);

  // Countdown local
  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (broadcastStatus.can_send || broadcastStatus.wait_seconds <= 0) return;
    countdownRef.current = setInterval(() => {
      setBroadcastStatus(prev => {
        const s = Math.max(0, (prev.wait_seconds || 0) - 1);
        if (s === 0) return { ...prev, wait_seconds: 0, can_send: true };
        return { ...prev, wait_seconds: s };
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [broadcastStatus.can_send, broadcastStatus.wait_seconds]);

  const openPreview = async () => {
    setPreviewLoading(true);
    setPreviewModal({ open: true, text: '', count: 0 });
    try {
      const r = await api.post(`/organizer-games/${gameId}/ratings-broadcast`, { top_n: topN, preview: true });
      setPreviewModal({ open: true, text: r.data?.text || '', count: r.data?.players_count || 0 });
    } catch (err) {
      message.error(err?.response?.data?.mensagem || err?.response?.data?.error || 'Erro ao gerar preview');
      setPreviewModal({ open: false, text: '', count: 0 });
    }
    setPreviewLoading(false);
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      const r = await api.post(`/organizer-games/${gameId}/ratings-broadcast`, { top_n: topN });
      message.success('Enviado no grupo!');
      setPreviewModal({ open: false, text: '', count: 0 });
      setBroadcastStatus({ can_send: false, wait_seconds: r.data?.cooldown_seconds || 21600 });
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === 'cooldown') {
        setBroadcastStatus({ can_send: false, wait_seconds: err.response.data.wait_seconds || 21600 });
        message.warning(err.response.data.mensagem);
      } else {
        message.error(err?.response?.data?.mensagem || 'Erro ao enviar');
      }
    }
    setSending(false);
  };

  const columns = [
    {
      title: 'Jogador', dataIndex: 'name', width: 180,
      render: v => <Text strong>{v}</Text>,
    },
    {
      title: 'Média', dataIndex: 'avg_rating', width: 200,
      render: v => v == null ? (
        <Text type="secondary">—</Text>
      ) : (
        <span>
          <Rate allowHalf disabled value={Number(v)} style={{ fontSize: 14 }} />
          <Text strong style={{ marginLeft: 8, color: '#faad14' }}>{v.toFixed(2)}</Text>
        </span>
      ),
    },
    {
      title: 'Avaliações', dataIndex: 'ratings_count', width: 110, align: 'center',
      render: v => v > 0 ? <Tag color="blue">{v}</Tag> : <Text type="secondary">0</Text>,
    },
  ];

  const totalRated = rows.filter(r => r.ratings_count > 0).length;
  const hasAnyRating = totalRated > 0;

  return (
    <>
      <Modal
        title={<span>⭐ Avaliações — <Tag>{gameLabel || `Jogo #${gameId}`}</Tag></span>}
        open={open}
        onCancel={onClose}
        footer={null}
        width={640}
      >
        {loading ? <Empty description="Carregando..." /> : rows.length === 0 ? (
          <Empty description="Nenhum jogador confirmado nesse jogo." />
        ) : (
          <>
            <div style={{
              marginBottom: 12, padding: 12, borderRadius: 10,
              background: '#f6ffed', border: '1px solid #b7eb8f',
              fontSize: 13,
            }}>
              <Text>
                {totalRated} de {rows.length} jogadores receberam avaliações.
                Avaliações são anônimas — você vê a média mas não quem deu qual nota.
              </Text>
            </div>

            {hasAnyRating && (
              <div style={{
                marginBottom: 12, padding: 12, borderRadius: 10,
                background: '#fafafa', border: '1px solid #e8e8e8',
                display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
              }}>
                <Text strong>Enviar no grupo:</Text>
                <Select
                  value={topN}
                  onChange={setTopN}
                  options={TOP_OPTIONS}
                  style={{ width: 200 }}
                  disabled={!broadcastStatus.can_send}
                />
                <Tooltip
                  title={broadcastStatus.can_send
                    ? 'Envia a lista dos destaques no grupo do WhatsApp'
                    : `Disponível em ${fmtCooldown(broadcastStatus.wait_seconds)}`}
                >
                  <Button
                    type="primary"
                    icon={<WhatsAppOutlined />}
                    onClick={openPreview}
                    disabled={!broadcastStatus.can_send}
                    style={{
                      background: broadcastStatus.can_send ? '#25D366' : undefined,
                      borderColor: broadcastStatus.can_send ? '#25D366' : undefined,
                      fontWeight: 600,
                    }}
                  >
                    {broadcastStatus.can_send
                      ? 'Enviar no grupo'
                      : `Disponível em ${fmtCooldown(broadcastStatus.wait_seconds)}`}
                  </Button>
                </Tooltip>
              </div>
            )}

            <Table
              columns={columns}
              dataSource={rows}
              rowKey="player_id"
              pagination={false}
              size="small"
            />
          </>
        )}
      </Modal>

      <Modal
        title={<span><SendOutlined /> Enviar avaliações no grupo</span>}
        open={previewModal.open}
        onCancel={() => setPreviewModal({ open: false, text: '', count: 0 })}
        onOk={confirmSend}
        confirmLoading={sending}
        okText="Enviar agora"
        cancelText="Cancelar"
        okButtonProps={{
          disabled: previewLoading || previewModal.count === 0,
          style: { background: '#25D366', borderColor: '#25D366', fontWeight: 600 },
          icon: <WhatsAppOutlined />,
        }}
        width={560}
      >
        <Alert
          type="warning" showIcon
          message="Cooldown de 6 horas"
          description="Depois de enviar, você só poderá enviar outra lista de avaliações daqui 6 horas."
          style={{ marginBottom: 12, borderRadius: 10 }}
        />
        {previewLoading ? (
          <Empty description="Gerando preview…" />
        ) : previewModal.count === 0 ? (
          <Empty description="Nenhum jogador com avaliações suficientes (mín 3)." />
        ) : (
          <div style={{
            background: '#e8f5e9', border: '1px solid #b7eb8f', borderRadius: 12,
            padding: 16, fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-wrap',
            maxHeight: 400, overflowY: 'auto',
          }}>
            {previewModal.text}
          </div>
        )}
      </Modal>
    </>
  );
};

export default GameRatingsModal;
